import uuid from 'uuid/v4';
import set from 'lodash/set';
import get from 'lodash/get';
import uniqBy from 'lodash/uniqBy';
import cloneDeep from 'lodash/cloneDeep';
import flattenDeep from 'lodash/flattenDeep';
import isEmpty from 'lodash/isEmpty';

import { getDefaultContentContext } from 'app/ContentContext/utils';
import { isExperiment } from 'app/Experiments/utils';
import { PAGE_TYPES, renderingEnvironmentsForComponent } from 'app/RenderingEnvironment/const';
import { USER_TAGS_LOCATION } from 'lib/const';
import { contractUtils, NUI_SCHEMA_NAMES } from 'lib/contract';
import { EN } from 'lib/locales';
import { createTags } from 'lib/string';

// constant to identify the status of component on the page in API
export const API_COMPONENT_ACTION_ADDED = 'added';
export const API_COMPONENT_ACTION_MODIFIED = 'modified';

// constants to identify user action on UI
export const UI_ADD_NEW_COMPONENT_TO_PAGE = 'UI_ADD_NEW_COMPONENT_TO_PAGE';
export const UI_ADD_MASTER_COMPONENT_TO_PAGE = 'UI_ADD_MASTER_COMPONENT_TO_PAGE';
export const UI_EDIT_COMPONENT_ON_PAGE = 'UI_EDIT_COMPONENT_ON_PAGE';

// constant to find page child components
const PAGE_CHILDREN = 'Payload.Components';

// these components are non dynamic components
// that are used as child components for dynamic container components
const forcedNonDynamicComponents = [
  NUI_SCHEMA_NAMES.COPY_ONLY_TILE,
  NUI_SCHEMA_NAMES.FOOTER_LINKS,
  NUI_SCHEMA_NAMES.GLOBAL_HEADER_COPY,
  NUI_SCHEMA_NAMES.GLOBAL_MESSAGING,
  NUI_SCHEMA_NAMES.SEO_CONTENT_BLOCK
];

/**
 * create a component object based on a component variation
 *
 * @param {object} opts - options object
 * @param {object} opts.canvasContract - canvas contract object for the component to be created
 * @param {string} [opts.componentVariation=''] - component variation for lookup in componentMap
 * @param {string} [opts.cwdSiteRoot=''] - cwd site root
 * @param {boolean} [opts.isMaster=false] - whether the component is a master component or not
 * @param {object} [opts.renderingEnv={}] - rendering env object
 * @param {string} [opts.renderingEnv.pageTemplateName=''] - rendering env page template name
 * @param {string} [opts.renderingEnv.pageType=''] - rendering env page type
 * @param {string} [opts.componentName=''] - component name
 * @param {string} [opts.templateName=''] - component template name
 * @param {string} [opts.userLanId=''] - user LAN id
 * @returns {object} new component object
 */
export const createNewComponentObject = function newComponentObjectFactory({
  canvasContract = {},
  componentVariation = '',
  cwdSiteRoot = '',
  isMaster = false,
  renderingEnv: { pageTemplateName = '', pageType = '' } = {},
  componentName = '',
  templateName = 'No Value',
  userLanId = ''
}) {
  const defaults = contractUtils.getDefaultsFromSection(canvasContract);
  const [attachChildInputLocation] = contractUtils.getPropLocation(
    canvasContract,
    'AttachChildInput'
  );
  const variation = componentVariation || canvasContract.displayName || canvasContract.name;

  const isDynamic =
    pageType === PAGE_TYPES.DYNAMIC && !forcedNonDynamicComponents.includes(variation);
  const isStatic = pageType === PAGE_TYPES.STATIC_CONTENT;

  const newComponentObject = {
    Action: API_COMPONENT_ACTION_ADDED,
    ChildLocation: attachChildInputLocation,
    ComponentName: componentName,
    Id: uuid(),
    IsDynamic: isDynamic || isStatic,
    IsMaster: isMaster,
    IsStaticContent: isStatic,
    Locale: EN,
    Payload: {
      ...defaults,
      ComponentTemplate: {
        TemplateName: defaults?.ComponentTemplate?.TemplateName ?? templateName
      }
    },
    RenderingEnv: {
      PageType: pageType,
      PageTemplateName: pageTemplateName
    },
    SchemaName: canvasContract.name,
    Tags: {
      NameTags: [],
      UserTags: [cwdSiteRoot, userLanId].filter(Boolean)
    },
    Variation: variation
  };

  // add default content context
  if (!canvasContract.customDisplayOptions || !newComponentObject.Payload.MetadataFields) {
    newComponentObject.Payload.MetadataFields = {
      ...defaults.MetadataFields,
      ContentContext: getDefaultContentContext({ componentVariation: variation, pageType })
    };
  }

  return newComponentObject;
};

/**
 * create a new page object
 *
 * @param {object} opts - options object
 * @param {string} opts.cwd - current working directory used to set parent folder id of page
 * @param {string} opts.cwdSiteRoot - name of the current working directory's site root
 * @param {string} [opts.id] - id of the new page
 * @param {boolean} [opts.isCompassPage=false] - whether or not the page is a compass page
 * @param {boolean} [opts.isDoNotArchive=false] - whether or not the page should be considered for archive
 * @param {boolean} [opts.pageName=''] - name of the new page
 * @param {object} [opts.renderingEnv={}] - rendering env object
 * @param {string} [opts.renderingEnv.pageTemplateName=''] - rendering env page template name
 * @param {string} [opts.renderingEnv.pageType=''] - rendering env page type
 * @returns {object} new page object
 */
export const createNewPageObject = function newPageObjectFactory({
  cwd,
  cwdSiteRoot,
  id = uuid(),
  isCompassPage = false,
  isDoNotArchive = false,
  pageName = '',
  renderingEnv: { pageTemplateName = '', pageType = '' } = {}
}) {
  return {
    Action: API_COMPONENT_ACTION_ADDED,
    Id: id,
    IsCompassPage: isCompassPage,
    IsDoNotArchive: isDoNotArchive,
    Locale: EN,
    Node: [],
    PageName: pageName,
    ParentFolderId: cwd,
    Payload: {
      Components: [],
      MetadataFields: {
        CategoryId: '',
        PageType: pageType,
        Route: {
          RouteMapping: []
        }
      },
      PageTemplateName: pageTemplateName
    },
    Tags: {
      NameTags: createTags(pageName),
      UserTags: [cwdSiteRoot].filter(Boolean)
    },
    Version: 2
  };
};

/**
 * transform container component payload for post/put to back-end
 * @param {object} component - the container component to be transformed
 * @returns {object} object with transformed data
 */
const transformComponentForApi = function transformComponentForApiFactory(component) {
  const { ChildLocation: childLocation, Payload: payload, SchemaName: schemaName } = component;
  const result = {
    ChildComponents: [],
    Payload: payload
  };
  let ids = [];
  if (childLocation) {
    if (schemaName === NUI_SCHEMA_NAMES.EXPERIMENT) {
      // transform variant data to get ids for result
      result.Payload.variants = payload.variants.map(({ data, variant }) => ({
        variant,
        Id: data.Id
      }));
      ids = result.Payload.variants.map(({ Id }) => Id);
    } else {
      // extract child IDs for ChildComponents and Payload
      ids = get(component, childLocation).map(item => item.Id);
      set(result, childLocation, ids);
    }
    result.ChildComponents = ids;
  }
  return result;
};

/**
 * Returns payload object for api to save component
 * @param {object} component - component to be added
 * @returns {object} returns payload object for api to save component
 */
const getTransformedComponentForApi = function getTransformedComponentForApi(component) {
  const transformedComponent = transformComponentForApi(cloneDeep(component));

  const apiComponent = {
    Action: component.Action,
    ChildComponents: transformedComponent.ChildComponents,
    ChildLocation: component.ChildLocation,
    ComponentName: component.ComponentName,
    ComponentRoute: component.ComponentRoute,
    ContentSize: component.ContentSize,
    Id: component.Id,
    IsCompassComponent: component.IsCompassComponent,
    IsDoNotArchive: component.IsDoNotArchive,
    IsDynamic: component.IsDynamic,
    IsMaster: component.IsMaster,
    IsStaticContent: component.IsStaticContent,
    LastTimestamp: component.LastTimestamp,
    Locale: component.Locale,
    NewComponents: [],
    Payload: transformedComponent.Payload,
    RenderingEnv: component.RenderingEnv,
    SchemaName: component.SchemaName,
    UserTags: get(component, USER_TAGS_LOCATION, []),
    Variation: component.Variation,
    Version: component.Version
  };

  return apiComponent;
};

/**
 * returns array of all of the descendants component in given object
 * @param {object} component - page or component object
 * @param {boolean} includeGivenComponent - true if given component needs to be included else false
 * @param {boolean} excludeSavedComponents - exclude already saved components
 * @returns {Array} returns array of all of the descendants component in given object
 */
const getAllComponents = function getAllComponents(
  component,
  includeGivenComponent,
  excludeSavedComponents
) {
  if (!component) {
    return [];
  }

  let sorterFunction;
  if (excludeSavedComponents) {
    sorterFunction = child => typeof child !== 'string';
  } else {
    sorterFunction = () => true;
  }

  const queue = [];
  const components = [];

  if (includeGivenComponent) {
    components.push(component);
  }

  queue.push(component);
  while (queue.length) {
    const currentComponent = queue.shift();

    let children = [];
    if (!currentComponent.SchemaName) {
      children = get(currentComponent, PAGE_CHILDREN, []);
    } else if (currentComponent.ChildLocation) {
      children = get(currentComponent, currentComponent.ChildLocation, []);

      // take the data property for experiments
      if (isExperiment(currentComponent)) {
        children = children.map(({ data }) => data);
      }
    }

    children.forEach(child => {
      if (sorterFunction(child)) {
        components.push(child);
        queue.push(child);
      }
    });
  }

  return components;
};

/**
 * returns array of components to be saved
 * @param {object} component - page or component object
 * @returns {Array} returns array of components to be saved
 */
const getChildrenComponentsForApi = function getChildrenComponentsForApi(component) {
  const rawComponents = getAllComponents(component, false);
  const componentsToBeSaved = rawComponents.filter(comp => comp.Action);
  const uniqueComponentsToBeSaved = uniqBy(componentsToBeSaved, comp => comp.Id);
  return uniqueComponentsToBeSaved.map(getTransformedComponentForApi);
};

/** Returns payload object for api to save component
 * @param {object} component - component to be added
 * @returns {object} returns payload object for api to save component
 */
const getComponentForApi = function getComponentForApiFactory(component) {
  const newComponents = getChildrenComponentsForApi(component);
  const transformedComponent = getTransformedComponentForApi(component);
  transformedComponent.NewComponents = newComponents;
  return transformedComponent;
};

/** Returns payload object for api to save page
 * @param {object} page - page to be saved
 * @returns {object} returns payload object for api to save component
 */
const getPageForApi = function getPageForApiFactory(page) {
  const {
    Payload: { Components, MetadataFields, PageTemplateName },
    Tags,
    Action,
    ...payload
  } = page;
  const apiPayload = {
    ...payload,
    NewComponents: getChildrenComponentsForApi(page),
    Payload: {
      MetadataFields,
      PageTemplateName,
      Components: (Components || []).map(x => x.Id)
    },
    UserTags: Tags.UserTags,
    LastTimestamp: page.LastTimestamp,
    History: []
  };

  return apiPayload;
};

/**
 * returns component after modifying recursively
 *
 * @param {object} component - page or container component object
 * @param {object} action - reducer action object
 * @param {boolean} isExperimentData - whether current call for experiment data (defaults to false)
 * @returns {object} returns component after modifying recursively
 */
const recursivelyModifyComponent = function recursivelyModifyComponent(
  component,
  action,
  isExperimentData = false
) {
  const processedComponent = component;

  if (processedComponent.Id === action.id) {
    set(processedComponent, action.schemaField, action.value);
    processedComponent.Action = processedComponent.Action || action.Action;
  }

  // always look at data property when working on experiment data
  const childrenLocation = isExperimentData
    ? 'data'
    : processedComponent.ChildLocation || PAGE_CHILDREN;

  const componentsList = get(processedComponent, childrenLocation);
  if (Array.isArray(componentsList)) {
    const updatedComponentsList = componentsList.map(comp =>
      recursivelyModifyComponent(comp, action, isExperiment(processedComponent))
    );
    return set(processedComponent, childrenLocation, updatedComponentsList);
  } else if (componentsList) {
    const updatedComponent = recursivelyModifyComponent(
      componentsList,
      action,
      isExperiment(processedComponent)
    );
    return set(processedComponent, childrenLocation, updatedComponent);
  }
  return processedComponent;
};

/**
 * creates a duplicate component object
 * @param {object} params - function params
 * @param {object} params.component - component object to be duplicated
 * @param {boolean} [params.isMaster=false] - is master value of the duplicated component
 * @param {string} [params.lanId=''] - new lan id for uesr tag of the duplicated component
 * @returns {object} duplicate component object
 */
const duplicateComponent = ({ component, isMaster = false, lanId = '' }) => {
  const clone = {
    ...cloneDeep(component),
    ComponentName: `Copy of ${component.ComponentName}`,
    History: [],
    Id: uuid(),
    Version: undefined,
    IsMaster: isMaster,
    Published: false,
    UsedInMultiplePlaces: false,
    Action: API_COMPONENT_ACTION_ADDED
  };

  // overwrite or add lan id user tag with current user
  if (lanId) {
    let lanIdInTags = false;
    const looksLikeLanId = /^[A-Z0-9]{4}$/s;
    const newTags = get(clone, USER_TAGS_LOCATION, []).map(tag => {
      if (tag.match(looksLikeLanId)) {
        lanIdInTags = true;
        return lanId;
      }
      return tag;
    });
    if (!lanIdInTags) {
      newTags.push(lanId);
    }
    set(clone, USER_TAGS_LOCATION, newTags);
  }

  return clone;
};

/**
 * recursively looks for component for the the given stack of ids
 * @param {object} pagePayload - page object
 * @param {Array} parentStack - stack of parent identifier
 * @returns {object} component
 */
const traversePageToFindComponent = function traversePageToFindComponent(
  pagePayload,
  parentStack
) {
  const queue = [...parentStack];
  let component = pagePayload;
  if (!pagePayload || parentStack.length === 0) {
    return undefined;
  }
  while (queue.length) {
    const childrenLocation = component.ChildLocation || PAGE_CHILDREN;
    const childrenComponents = get(component, childrenLocation);
    if (childrenComponents && childrenComponents.length > 0) {
      const doChildrenHaveId = childrenComponents.some(comp => comp.Id !== undefined);
      if (doChildrenHaveId) {
        const { Id } = queue.shift();
        component = childrenComponents.find(comp => comp.Id === Id);
      } else {
        [component] = childrenComponents;
      }
    } else {
      return undefined;
    }

    if (!component) {
      return undefined;
    }
  }

  return component;
};

/** returns a dummy page object by putting component onto
 * @param {object} component - component to be placed on dummy page
 * @returns {object} returns a dummy page object by putting component onto
 */
const slidePageUnderTheComponent = function slidePageUnderTheComponent(component) {
  return {
    PageName: 'Dummy page to render component',
    Payload: {
      Components: [component],
      MetadataFields: {
        PageType:
          component.RenderingEnv.PageType ||
          renderingEnvironmentsForComponent[0].value.pageType
      },
      PageTemplateName:
        component.RenderingEnv.PageTemplateName ||
        renderingEnvironmentsForComponent[0].value.pageTemplateName
    }
  };
};

/**
 * helper function to return list of desktop and mobile image urls along with context.
 * @param {object} component - component object
 * @param {object} contractMap - contract mapping
 * @returns {object} returns the list of desktop and mobile image urls along with context
 */
const locationMap = function locationMap(component, contractMap) {
  const contract = contractMap[component.Variation || component.SchemaName];
  if (!contract) {
    return [];
  }
  let mobileUrl = null;
  let desktopUrl = null;
  const imageLocations = flattenDeep(contractUtils.getPropLocation(contract, 'AMPConnector'));

  // determine what displays have been chosen
  let displayOnMobile = false;
  let displayOnDesktop = false;
  component.Payload.MetadataFields.ContentContext.Device.forEach(display => {
    if (display === 'Display On Mobile') {
      displayOnMobile = true;
    } else {
      displayOnDesktop = true;
    }
  });

  // construct the url array
  const componentUrl = [];

  switch (component.SchemaName) {
    case NUI_SCHEMA_NAMES.TILE:
      imageLocations.forEach(location => {
        // Check if there are mobile or desktop images
        // (Tile specific; only component with exactly one image for mobile or desktop)
        if (location === 'Payload.ImageLink.MobileImage_Url.ImageUrl') {
          mobileUrl = get(component, location, null);
        } else {
          desktopUrl = get(component, location, null);
        }
      });

      if (!isEmpty(desktopUrl) && displayOnDesktop) {
        componentUrl.push({
          imageUrl: desktopUrl,
          context: 'desktop'
        });
      }
      if (!isEmpty(mobileUrl) && displayOnMobile) {
        componentUrl.push({
          imageUrl: mobileUrl,
          context: 'mobile'
        });
      }
      break;
    default:
      // Other component types that have no separate mobile link, therefore show up for both.
      // May have multiple media fields.
      // Display depends only on whether Display For is selected.
      imageLocations.forEach(location => {
        const mediaUrl = get(component, location, null);
        if (!isEmpty(mediaUrl) && displayOnDesktop) {
          componentUrl.push({
            imageUrl: mediaUrl,
            context: 'desktop'
          });
        }
        if (!isEmpty(mediaUrl) && displayOnMobile) {
          componentUrl.push({
            imageUrl: mediaUrl,
            context: 'mobile'
          });
        }
      });
      break;
  }

  return componentUrl;
};

/**
 * returns the list of desktop and mobile image urls along with context for a component.
 *
 * @param {object} component - component object
 * @param {object} contractMap - contract mapping
 * @returns {object} returns the list of desktop and mobile image urls along with context.
 */
const componentImageLocation = function componentImageLocation(component, contractMap) {
  return locationMap(component, contractMap);
};

/**
 * returns given array of components with isFavorite prop.
 * @param {Array} components - array of components to be updated with isFavorite prop
 * @param {Array} favoriteComponents - array of favorite components
 * @returns {Array} returns given array of components with isFavorite prop.
 */
const populateIsFavoriteComponent = function populateIsFavoriteComponent(
  components,
  favoriteComponents
) {
  const isFavoriteComponentsArray = Array.isArray(favoriteComponents);

  if (Array.isArray(components)) {
    const updatedComponent = components.map(c => ({
      ...c,
      isFavorite: isFavoriteComponentsArray && favoriteComponents.some(fc => fc.Id === c.Id)
    }));
    return updatedComponent;
  }
  return components;
};

/**
 * adds disabled property to given components
 * @param {Array} components - array of components to update disabled property
 * @param {boolean} isPage - true is page is selected
 * @param {boolean} isContainer - true if a container component is selected
 * @param {boolean} handlingFavorites - true if handling favorite components
 * @returns {object[]} array of components with disabled property
 */
const disabledComponents = function disabledComponents(
  components,
  isPage,
  isContainer,
  handlingFavorites
) {
  return components.map(c => {
    const ret = { ...c };
    if (isPage || isContainer) {
      ret.disabled = c.isReadonly;
      if (handlingFavorites) {
        ret.isFavorite = true;
      }
    } else {
      ret.disabled = false;
    }
    return ret;
  });
};

export {
  componentImageLocation,
  disabledComponents,
  duplicateComponent,
  getAllComponents,
  getChildrenComponentsForApi,
  getComponentForApi,
  getPageForApi,
  locationMap,
  populateIsFavoriteComponent,
  traversePageToFindComponent,
  recursivelyModifyComponent,
  slidePageUnderTheComponent
};
